<script setup lang="ts">
import {ref, useTemplateRef, watch} from "vue";
import {useEventListener, useTimeoutFn} from "@vueuse/core"
import {LControl, LIcon, LMap, LMarker, LPopup, LTileLayer} from "@vue-leaflet/vue-leaflet";
import L from "leaflet";
import {IStoreDefault, IStoreDistanceDefault} from "@/api/aj-trends.model.ts";
import {germanyMaxBounds} from "@/service/userLocation.service.ts";
import {WebLinks} from "@/service/webLinks.ts";
import osmAttribution from "@/assets/osm-attribution.html?raw"

const props = defineProps<{
  store: IStoreDefault,
  nearby: ReadonlyArray<IStoreDistanceDefault>,
}>()

const mapRef = useTemplateRef<InstanceType<typeof LMap>>("mapRef")
const popupRef = useTemplateRef<InstanceType<typeof LPopup>>("popupRef")

function handlePopupReady() {
  const map = mapRef.value?.leafletObject as undefined | L.Map
  const popup = popupRef.value?.leafletObject as undefined | L.Popup
  if (map && popup) {
    popup.openOn(map)
  }
}

function handleCenterOnStore(click: MouseEvent) {
  click.preventDefault()
  const store = props.store
  const map = mapRef.value?.leafletObject as undefined | L.Map
  map?.flyTo(L.latLng(store.lat, store.lon), 16, {animate: true, duration: 1})
}

const storeDistanceKilometersFormat = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 2,
})

const storeDistanceMetersFormat = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

function formatStoreDistance(distance: number): string {
  if (distance > 1000) {
    return storeDistanceKilometersFormat.format(distance / 1000) + " km"
  } else {
    return storeDistanceMetersFormat.format(distance) + " m"
  }
}

const defaultIconProps = {
  iconAnchor: [12, 41] as L.PointExpression,
  iconSize: [25, 41] as L.PointExpression,
  popupAnchor: [0, -40] as L.PointExpression,
}

const mapScrollBlocked = ref(false)
const unblockMapScroll = () => void (mapScrollBlocked.value = false)
const mapScrollTimeout = useTimeoutFn(unblockMapScroll, 1_000)

watch(mapScrollBlocked, blocked => {
  const map = mapRef.value?.leafletObject as undefined | L.Map
  if (blocked) map?.scrollWheelZoom?.disable?.()
  else map?.scrollWheelZoom?.enable?.()
})

useEventListener(window, "scroll", () => {
  mapScrollTimeout.stop()
  mapScrollBlocked.value = true
  mapScrollTimeout.start()
})
</script>

<template>
  <LMap
      ref="mapRef"
      style="width: 100%; height: 600px;"
      :zoom="16"
      :center="[store.lat, store.lon]"
      :options="{ minZoom: 7, maxZoom: 18, maxBounds: germanyMaxBounds, maxBoundsViscosity: 0 }"
      @click="unblockMapScroll()"
  >
    <LControl position="topright" class="leaflet-bar">
      <a
          href="#"
          title="Zurück zum Markt"
          role="button"
          aria-disabled="false"
          @click="handleCenterOnStore($event)"
      >
        <span aria-hidden="true">&#x1F6D2;</span>
      </a>
    </LControl>
    <LTileLayer
        name="OpenStreetMap"
        url="https://tileserver.werdenktwas.de/styles/test-style/{z}/{x}/{y}.png"
        :attribution="osmAttribution as string"
        layer-type="base"
    />
    <LMarker :lat-lng="[store.lat, store.lon]" :options="{ interactive: false }">
      <LIcon icon-url="web-components/leaflet-icon.png" v-bind="defaultIconProps"/>
      <!--<LIcon icon-url="assets/SMT-Icon.png" :icon-size="[160, 200]" :icon-anchor="[80, 200]"/>-->
      <LPopup
          ref="popupRef"
          :lat-lng="[store.lat, store.lon]"
          :options="{closeButton: false, closeOnEscapeKey: false, closeOnClick: false, autoClose: false}"
          @ready="handlePopupReady()"
      >
        <a :href="store.website || undefined" target="_blank">
          <div>{{ store.name }}</div>
          <div>{{ store.street }}</div>
          <div>{{ store.postcode }} {{ store.city }}</div>
        </a>
      </LPopup>
    </LMarker>
    <template v-for="n of nearby" :key="n.destinationStore.id">
      <LMarker :lat-lng="[n.destinationStore.lat, n.destinationStore.lon]">
        <LIcon icon-url="web-components/leaflet-icon-nearby.png" v-bind="defaultIconProps"/>
        <LPopup>
          <a :href="WebLinks.storeLink(n.destinationStore)" target="_self">
            <div>&#9658; <b style="text-decoration: underline">{{ formatStoreDistance(n.distance) }}</b></div>
            <div>{{ n.destinationStore.name }}</div>
            <div>{{ n.destinationStore.street }}</div>
            <div>{{ n.destinationStore.postcode }} {{ n.destinationStore.city }}</div>
          </a>
        </LPopup>
      </LMarker>
    </template>
  </LMap>
</template>
